/**
 * Die eine Entscheidung über die Messung — ihr Wortlaut, ihr Nachweis und der
 * Ort, an dem sie liegt.
 *
 * Both surfaces measure with the same Analytics property (`analytics.ts`), so
 * they ask one question and not two. Everything that makes that one question
 * one is here: the wording that is shown, the record that is kept, and the
 * cookie both hosts read.
 *
 * The rules are legal, not preferences: § 165 Abs 3 TKG 2021 and Art 6 Abs 1
 * lit a DSGVO want a prior yes, Art 7 Abs 1 DSGVO wants it provable, Art 7
 * Abs 3 DSGVO wants withdrawing to be as easy as giving, and the six months
 * after which the question comes back are promised in `docs/DATENSCHUTZ.md`.
 * A second implementation of any of that would be a second chance to get it
 * wrong, silently and in the measuring direction.
 */
/** After this many days the question is asked again. */
export const CONSENT_MAX_AGE_DAYS = 180;
const MAX_AGE_MS = CONSENT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
/**
 * Die Fassung des Wortlauts, zu dem zugestimmt wurde.
 *
 * Bump it in the same commit as any change to `CONSENT_NOTICE`: the record
 * names what was agreed to, and a record that names wording nobody can
 * reproduce proves nothing. A stored record of another Fassung counts as never
 * asked, so everyone is asked again — which is the point.
 */
export const CONSENT_FASSUNG = '2026-08-18';
/**
 * Der Wortlaut der Frage — die eine Stelle, an der er steht.
 *
 * Shaped by what the authorities actually check:
 *
 *   - EDPB cookie banner taskforce (2023-01): say what it is for, and let
 *     refusing be a button on the first layer — not a link, not behind
 *     „Einstellungen".
 *   - DSK, Orientierungshilfe Telemedien: a bare „Okay" is no declaration, and
 *     „Akzeptieren" fails when the text does not say what is being agreed to.
 *   - DSB decisions and onlinesicherheit.gv.at: both options on the same layer
 *     with the same optical weight, nothing pre-selected, and the way back
 *     named in the question itself.
 *
 * The text describes what both surfaces measure, because the yes covers both
 * (see `consentCookieDomain`) and a purpose that is not stated is not
 * consented to. It is split around the link because each surface has its own
 * address for the Datenschutzerklärung — the sentence is the same either way.
 */
export const CONSENT_NOTICE = {
    titel: 'Messung mit Google Analytics',
    vorLink: 'Wir möchten mit Google Analytics messen, welche Seiten gelesen werden, wie viele Besucher ' +
        'sich registrieren und wie oft daraus ein Abo wird – und ob eine Anzeige daran beteiligt ' +
        'war. Google erhält dabei Daten über Ihren Besuch, auch in den USA, ohne Ihren Namen und ' +
        'ohne Daten aus Ihrer Buchhaltung. Werbung wird Ihnen deswegen nicht anders angezeigt und ' +
        'keine Zielgruppe gebildet. Ohne Ihre Erlaubnis wird nichts geladen und nichts gemessen; ' +
        'easyrechnung funktioniert genauso. Was dabei verarbeitet wird, steht in der ',
    linkText: 'Datenschutzerklärung',
    nachLink: '.',
    ablehnen: 'Messung ablehnen',
    erlauben: 'Messung erlauben',
    hinweis: 'Die Entscheidung gilt sechs Monate, auf easyrechnung.at und in easyrechnung. Ändern können ' +
        'Sie sie jederzeit: auf der Website über „Cookie-Einstellungen" im Fußbereich, in ' +
        'easyrechnung unter Einstellungen → Daten und Datenschutz.',
};
/**
 * What gets written when someone decides.
 *
 * Not a flag: Art 7 Abs 1 DSGVO makes us able to show *that* the yes happened
 * and *to what* — hence the moment and the Fassung of the wording that was on
 * screen.
 */
export function serializeConsent(choice, now) {
    return JSON.stringify({ choice, at: now.toISOString(), fassung: CONSENT_FASSUNG });
}
/**
 * Reads a stored value back into a state, treating anything else as never
 * asked.
 *
 * Junk, an expired record, a legacy format, a record of an older Fassung: all
 * come back as `unknown`, which is the safe direction — it can only lead to
 * asking once more, never to measuring without a yes.
 */
export function parseStoredConsent(raw, now) {
    if (!raw)
        return 'unknown';
    let record;
    try {
        record = JSON.parse(raw);
    }
    catch {
        return 'unknown';
    }
    if (typeof record !== 'object' || record === null)
        return 'unknown';
    const { choice, at, fassung } = record;
    if (choice !== 'granted' && choice !== 'denied')
        return 'unknown';
    if (fassung !== CONSENT_FASSUNG)
        return 'unknown';
    const decidedAt = typeof at === 'string' ? Date.parse(at) : Number.NaN;
    if (Number.isNaN(decidedAt))
        return 'unknown';
    if (now.getTime() - decidedAt > MAX_AGE_MS)
        return 'unknown';
    return choice;
}
/** Der Name des Cookies, das beide Oberflächen lesen und schreiben. */
export const CONSENT_COOKIE = 'er_consent';
const SITE_DOMAIN = 'easyrechnung.at';
/**
 * Die Domain, auf der das Cookie liegt — und damit der Grund, warum es ein
 * Cookie ist und kein Eintrag im lokalen Speicher.
 *
 * `easyrechnung.at` and `app.easyrechnung.at` are two origins, and local
 * storage belongs to one origin. Somebody who has just answered the question
 * on the website would be asked it again the moment they sign in, and a
 * refusal on one host would say nothing about the other. Written on the parent
 * domain, the record is one record: read on both hosts, withdrawn on both at
 * once.
 *
 * `null` for anything else — `localhost` in development, a preview host, an IP
 * address. A cookie without a Domain attribute belongs to the host that set
 * it, which is the only safe answer for a name we do not own; on `localhost`
 * it is also shared across ports, so the two dev servers see one decision
 * anyway.
 */
export function consentCookieDomain(hostname) {
    if (hostname === SITE_DOMAIN || hostname.endsWith(`.${SITE_DOMAIN}`))
        return SITE_DOMAIN;
    return null;
}
function attributes(host) {
    const domain = consentCookieDomain(host.hostname);
    return [
        'Path=/',
        // Lax, not Strict: somebody who follows the link from easyrechnung.at into
        // the application arrives in a cross-site navigation, and a Strict cookie
        // would not be sent with it — the first page inside would ask again.
        'SameSite=Lax',
        ...(domain ? [`Domain=${domain}`] : []),
        ...(host.protocol === 'https:' ? ['Secure'] : []),
    ].join('; ');
}
/**
 * The string to assign to `document.cookie` for a decision.
 *
 * Not `HttpOnly`, and it cannot be: both surfaces decide in the browser and
 * read it back there. Nothing about the person is in it — the choice, the
 * moment, the Fassung — and the API ignores it, although the browser now sends
 * it along with every request to `app.easyrechnung.at`.
 */
export function consentCookieAssignment(choice, now, host) {
    const value = encodeURIComponent(serializeConsent(choice, now));
    const maxAge = CONSENT_MAX_AGE_DAYS * 24 * 60 * 60;
    return `${CONSENT_COOKIE}=${value}; Max-Age=${maxAge}; ${attributes(host)}`;
}
/**
 * The string that removes it again, on withdrawal.
 *
 * Same Domain and Path as above, or the browser keeps the old cookie and adds
 * an expired second one next to it.
 */
export function consentCookieRemoval(host) {
    return `${CONSENT_COOKIE}=; Max-Age=0; ${attributes(host)}`;
}
/** Picks the record out of a `document.cookie` string. */
export function readConsentCookie(cookie) {
    if (!cookie)
        return null;
    for (const part of cookie.split(';')) {
        const eq = part.indexOf('=');
        if (eq < 0)
            continue;
        if (part.slice(0, eq).trim() !== CONSENT_COOKIE)
            continue;
        try {
            return decodeURIComponent(part.slice(eq + 1).trim());
        }
        catch {
            // A value that is not valid percent-encoding is not one of ours.
            return null;
        }
    }
    return null;
}
//# sourceMappingURL=consent.js.map